import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import {
  openSupportPortal,
  useSelectSupportPlan,
  useSupportPlans,
  useSupportSubscription,
} from "@/lib/support";

export default function SupportPlanPage() {
  const { t } = useTranslation();
  const { data: plans } = useSupportPlans();
  const { data: sub } = useSupportSubscription();
  const select = useSelectSupportPlan();
  const [params] = useSearchParams();
  const checkout = params.get("checkout");

  const priceLine = (plan: { monthly_price_eur: string; hourly_price_eur: string }) => {
    const monthly = Number(plan.monthly_price_eur);
    const hourly = Number(plan.hourly_price_eur);
    if (hourly > 0) return t("support.plan.perHour", { price: hourly.toFixed(0) });
    if (monthly > 0) return t("support.plan.perMonth", { price: monthly.toFixed(0) });
    return t("support.plan.free");
  };
  const slaLine = (hours: number) => {
    if (hours === 0) return t("support.sla.instant");
    if (hours >= 168) return t("support.sla.days", { count: Math.round(hours / 24) });
    return t("support.sla.hours", { count: hours });
  };

  return (
    <AppLayout
      title={t("support.plan.title")}
      description={t("support.plan.subtitle")}
    >
      <BackLink fallback="/support" className="text-sm text-cvp-accent">← {t("support.tickets.title")}</BackLink>

      {checkout === "success" && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          {t("support.plan.checkoutSuccess")}
        </div>
      )}
      {checkout === "cancel" && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
          {t("support.plan.checkoutCancel")}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {(plans ?? [])
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((plan) => {
            const current = sub?.plan.slug === plan.slug;
            return (
              <div key={plan.slug} className={"card flex flex-col " + (current ? "border-cvp-accent" : "")}>
                <div className="text-lg font-semibold text-nb-text">{plan.name}</div>
                <div className="text-2xl font-bold text-nb-text">{priceLine(plan)}</div>
                <div className="mt-1 text-sm text-nb-text-2">
                  {slaLine(plan.first_response_sla_hours)}
                </div>
                <p className="mt-2 flex-1 text-sm text-nb-text-2">{plan.description}</p>
                {current ? (
                  <span className="mt-3 text-center text-sm font-medium text-cvp-accent">
                    {t("support.plan.current")}
                  </span>
                ) : (
                  <button
                    className="btn-primary mt-3"
                    disabled={select.isPending}
                    onClick={() => select.mutate(plan.slug)}
                  >
                    {select.isPending ? t("support.plan.switching") : t("support.plan.choose")}
                  </button>
                )}
              </div>
            );
          })}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-nb-text-2">{t("support.plan.billingNote")}</p>
        <button
          type="button"
          className="shrink-0 text-sm text-cvp-accent hover:underline"
          onClick={() => void openSupportPortal()}
        >
          {t("support.plan.viewInvoices")}
        </button>
      </div>
    </AppLayout>
  );
}
